import { fmtMoney, fmtNumber, fmtPct } from '../presentation/format';

/**
 * Compliance-certificate lines with their unit (F-LR-052). The service now writes `unit` and the
 * pre-formatted `reportedText` / `computedText` on each line; a certificate stored before that
 * carries bare numbers, so the unit is read from the covenant's name when the line has none.
 */

export type CertificateUnit = 'PCT' | 'MONEY' | 'RATIO' | 'COUNT';

export interface CertificateLine {
  covenantKey?: string | null;
  covenantName?: string | null;
  reportedValue?: number | string | null;
  computedValue?: number | string | null;
  status?: string | null;
  citation?: string | null;
  unit?: string | null;
  reportedText?: string | null;
  computedText?: string | null;
}

const PERCENT_NAME = /ratio|rate|%|pct|delinquen|loss/i;

/** The line's own unit when present; otherwise percent for ratio/rate/delinquency/loss names, money for the rest. */
export const certificateLineUnit = (line: CertificateLine): CertificateUnit => {
  const stated = String(line.unit || '').toUpperCase();
  if (stated === 'PCT' || stated === 'PERCENT' || stated === 'PERCENTAGE') return 'PCT';
  if (stated === 'MONEY' || stated === 'AMOUNT' || stated === 'CURRENCY') return 'MONEY';
  if (stated === 'RATIO' || stated === 'MULTIPLE') return 'RATIO';
  if (stated === 'COUNT' || stated === 'DAYS' || stated === 'NUMBER') return 'COUNT';
  return PERCENT_NAME.test(`${line.covenantName || ''} ${line.covenantKey || ''}`) ? 'PCT' : 'MONEY';
};

export const certificateUnitWord = (unit: CertificateUnit, currency?: string | null): string =>
  unit === 'PCT' ? 'percent' : unit === 'RATIO' ? 'ratio (x)' : unit === 'COUNT' ? 'count' : (currency || 'USD');

/** The borrower's or the engine's figure as text with its unit, or the sentence for an absent one. */
export const certificateValueText = (line: CertificateLine, which: 'reported' | 'computed', currency?: string | null): string => {
  const text = which === 'reported' ? line.reportedText : line.computedText;
  if (text) return String(text);
  const value = which === 'reported' ? line.reportedValue : line.computedValue;
  if (value === null || value === undefined || value === '') return which === 'reported' ? 'Not reported' : 'Not computed';
  switch (certificateLineUnit(line)) {
    case 'PCT': return fmtPct(value, 2);
    case 'RATIO': return `${fmtNumber(value, 2)}x`;
    case 'COUNT': return fmtNumber(value, 0);
    default: return fmtMoney(value, currency || 'USD');
  }
};
